import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/controls/LanguageSwitcher";
import { ThemeToggle } from "@/components/controls/ThemeToggle";
import { Logo } from "@/components/brand/Logo";

interface Props {
  step: number;
  total?: number;
  children: ReactNode;
  showSkip?: boolean;
  onSkip?: () => void;
}

/**
 * Minimal shell used for /onboarding/* screens. Top bar shows brand, a
 * 3-segment progress indicator and an optional "Skip" link. No sidebar.
 */
export function OnboardingShell({ step, total = 4, children, showSkip = false, onSkip }: Props) {
  const { t } = useTranslation();
  const safeTotal = Math.max(1, total);
  const safeStep = Math.min(Math.max(1, step), safeTotal);
  return (
    <div className="flex min-h-screen w-full flex-col bg-background text-foreground">
      <header className="border-b border-border">
        <div className="container flex h-16 items-center justify-between gap-4">
          <Logo size={32} />
          <div className="flex items-center gap-3">
            <span className="hidden text-xs uppercase tracking-wider text-muted-foreground sm:inline">
              {t("onboarding.stepOf", { current: safeStep, total: safeTotal })}
            </span>
            <div className="flex items-center gap-1.5" aria-hidden>
              {Array.from({ length: safeTotal }, (_, idx) => idx + 1).map((i) => (
                <div
                  key={i}
                  className={`h-1 rounded-full transition-all ${
                    i < safeStep
                      ? "w-6 bg-primary"
                      : i === safeStep
                        ? "w-10 bg-primary/70"
                        : "w-6 bg-muted"
                  }`}
                />
              ))}
            </div>
            <div className="hidden items-center gap-1 sm:flex">
              <LanguageSwitcher />
              <ThemeToggle />
            </div>
            {showSkip ? (
              <button
                type="button"
                onClick={onSkip}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                {t("onboarding.skip")}
              </button>
            ) : null}
          </div>
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-4xl">{children}</div>
      </main>
    </div>
  );
}
